import { useCallback, useMemo, useState } from 'react';
import {
  useExternalOrders,
  useExternalOrder,
  useAuthorizeExternalOrder,
  useCreateExternalOrder,
  useCheckExternalOrderStock,
  useCancelExternalOrder,
  useSetExternalOrderConsumptionAuthorization,
} from '../../../hooks/useExternalOrders';
import { useWarehouses, useWarehouse } from '../../../hooks/useWarehouses';
import { useProjectOptions, useProject } from '../../../hooks/useProjects';
import {
  ITEMS_PER_PAGE,
  INITIAL_FILTERS,
  getInitialOrderState,
} from '../constants/internalOrdersConstants';
import {
  buildCreateOrderPayload,
  mapExternalOrderToDisplay,
} from '../utils/internalOrdersUtils';

export const useInternalOrdersTable = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(getInitialOrderState);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const createModalOpen = dialogOpen;

  const {
    data: ordersResponse,
    isLoading: ordersLoading,
  } = useExternalOrders({
    page,
    per_page: rowsPerPage,
    search: searchTerm,
    ...filters,
  });
  const { data: detailOrder, isLoading: detailOrderLoading, refetch: refetchDetailOrder } = useExternalOrder(
    selectedOrderId,
    {
      enabled: detailDialogOpen && Boolean(selectedOrderId),
    }
  );
  const { data: detailProject } = useProject(detailOrder?.uuid_project, {
    enabled: detailDialogOpen && Boolean(detailOrder?.uuid_project),
  });
  const { data: detailWarehouse } = useWarehouse(
    detailOrder?.uuid_destination_warehouse,
    {
      enabled: detailDialogOpen && Boolean(detailOrder?.uuid_destination_warehouse),
    }
  );
  const { data: warehouses = [], isLoading: warehousesLoading } = useWarehouses({
    enabled: true,
  });
  const { data: projects = [], isLoading: projectsLoading } = useProjectOptions({
    enabled: true,
  });

  const createOrderMutation = useCreateExternalOrder();
  const authorizeOrderMutation = useAuthorizeExternalOrder();
  const checkStockMutation = useCheckExternalOrderStock();
  const consumptionAuthorizationMutation = useSetExternalOrderConsumptionAuthorization();
  const cancelOrderMutation = useCancelExternalOrder();

  const orders = useMemo(() => {
    if (Array.isArray(ordersResponse)) return ordersResponse;
    return ordersResponse?.results || [];
  }, [ordersResponse]);

  const data = useMemo(
    () => orders.map((order) => mapExternalOrderToDisplay(order, [], [])),
    [orders]
  );

  const totalCount = Array.isArray(ordersResponse)
    ? data.length
    : ordersResponse?.total || 0;
  const totalPages = Math.max(
    1,
    Array.isArray(ordersResponse)
      ? Math.ceil(totalCount / rowsPerPage)
      : ordersResponse?.pages || 1
  );

  const selectedOrderDetail = useMemo(() => {
    if (!detailDialogOpen || !selectedOrderId || !detailOrder) return null;

    return mapExternalOrderToDisplay(detailOrder, [], [], {
      warehouse: detailWarehouse,
      project: detailProject,
    });
  }, [
    detailDialogOpen,
    selectedOrderId,
    detailOrder,
    detailWarehouse,
    detailProject,
  ]);

  const isLoading = ordersLoading;
  const isSaving = createOrderMutation.isPending;
  const isProcessing =
    authorizeOrderMutation.isPending ||
    checkStockMutation.isPending ||
    consumptionAuthorizationMutation.isPending ||
    cancelOrderMutation.isPending;

  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPage(1);
  }, []);

  const handlePageChange = useCallback((_, newPage) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  }, []);

  const handleOpenDialog = useCallback(() => {
    setSelectedOrder(getInitialOrderState());
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedOrder(getInitialOrderState());
  }, []);

  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setSelectedOrder((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSelectChange = useCallback((event) => {
    const { name, value } = event.target;
    setSelectedOrder((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleAddMaterial = useCallback((material) => {
    if (!material?.value) return;

    setSelectedOrder((prev) => {
      const exists = (prev.materials || []).some(
        (item) => item.materialId === material.value
      );
      if (exists) return prev;

      return {
        ...prev,
        materials: [
          ...(prev.materials || []),
          {
            materialId: material.value,
            code: material.code,
            name: material.name,
            unit: material.unit,
            quantity: 1,
          },
        ],
      };
    });
  }, []);

  const handleUpdateMaterialQuantity = useCallback((materialId, quantity) => {
    setSelectedOrder((prev) => ({
      ...prev,
      materials: (prev.materials || []).map((item) =>
        item.materialId === materialId
          ? { ...item, quantity: quantity === '' ? '' : Number(quantity) }
          : item
      ),
    }));
  }, []);

  const handleRemoveMaterial = useCallback((materialId) => {
    setSelectedOrder((prev) => ({
      ...prev,
      materials: (prev.materials || []).filter(
        (item) => item.materialId !== materialId
      ),
    }));
  }, []);

  const handleSave = useCallback(async () => {
    const payload = buildCreateOrderPayload(selectedOrder);
    if (!payload) return false;

    const createdOrder = await createOrderMutation.mutateAsync(payload);
    const createdOrderId = createdOrder?.uuid;

    setDialogOpen(false);
    setSelectedOrder(getInitialOrderState());

    if (createdOrderId) {
      setSelectedOrderId(createdOrderId);
      setDetailDialogOpen(true);
    }

    return true;
  }, [selectedOrder, createOrderMutation]);

  const handleViewDetail = useCallback((order) => {
    setSelectedOrderId(order.uuid);
    setDetailDialogOpen(true);
  }, []);

  const handleCloseDetailDialog = useCallback(() => {
    setDetailDialogOpen(false);
    setSelectedOrderId(null);
  }, []);

  const handleCheckStock = useCallback(async (orderId) => {
    return checkStockMutation.mutateAsync(orderId);
  }, [checkStockMutation]);

  const handleAuthorizeOrder = useCallback(async (orderId) => {
    return authorizeOrderMutation.mutateAsync(orderId);
  }, [authorizeOrderMutation]);

  const handleSetConsumptionAuthorization = useCallback(async (orderId, value = true) => {
    return consumptionAuthorizationMutation.mutateAsync({
      id: orderId,
      consumption_authorized: value,
    });
  }, [consumptionAuthorizationMutation]);

  const handleRefreshDetailOrder = useCallback(async () => {
    await refetchDetailOrder();
  }, [refetchDetailOrder]);

  const handleCancelOrder = useCallback(async ({ id, reason }) => {
    return cancelOrderMutation.mutateAsync({ id, reason });
  }, [cancelOrderMutation]);

  return {
    data,
    isLoading,
    isSaving,
    isProcessing,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    searchTerm,
    filters,
    dialogOpen,
    detailDialogOpen,
    selectedOrder,
    selectedOrderDetail,
    detailOrderLoading,
    warehouses,
    projects,
    projectsLoading,
    warehousesLoading,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleInputChange,
    handleSelectChange,
    handleAddMaterial,
    handleUpdateMaterialQuantity,
    handleRemoveMaterial,
    handleSave,
    handleViewDetail,
    handleCloseDetailDialog,
    handleAuthorizeOrder,
    handleCheckStock,
    handleSetConsumptionAuthorization,
    handleRefreshDetailOrder,
    handleCancelOrder,
  };
};
